<template>
  <main-layout
    controlsBg="light"
    :hasControlsBar="false"
    :breadcrumb="getBreadcrumb">
    <div class="container">
      <grid-row column-align="center">
        <grid-col col-size="4">
          <h1 class="header margin-top-xl margin-bottom-xl">
            <chevron
              @click="$router.push({name: 'Settings'})"/>
            {{ $t('personalArea.profile') }}: {{ getUserAccount.username }}
          </h1>
          <alert
            v-if="alertVisible"
            :message="alertMessage"
            :type="alertType">
          </alert>
        </grid-col>
      </grid-row>
      <form
        v-on:submit.prevent
        v-on:keypress.enter.prevent="onSave">
        <grid-row column-align="center" col-spaced>
          <grid-col col-size="2">
            <h2 class="margin-top-m">{{ $t('personalArea.updatePassword') }}</h2>
            <div class="variable-input-field variable-input-field-name">
              <label-input
                form-size="m"
                :label="$t('userProfile.oldPassword')"
                v-model="oldPassword"
                :type="'password'"
                :error="oldPasswordError"
                :errorMessage="$t('userProfile.passwordWrong')">
              </label-input>
            </div>
          </grid-col>
          <grid-col col-size="2"></grid-col>
        </grid-row>
        <grid-row column-align="center" col-spaced>
          <grid-col col-size="2">
            <div class="variable-input-field variable-input-field-name">
              <label-input
                ref="passwordInput"
                form-size="m"
                :label="$t('userProfile.newPassword')"
                v-model="password"
                :type="'password'"
                :error="passwordError"
                :errorMessage="$t('user.passwordFormatMessage')">
              </label-input>
            </div>
          </grid-col>
          <grid-col col-size="2">
            <div class="variable-input-field variable-input-field-name">
              <label-input
                ref="passwordRepeatInput"
                form-size="m"
                :label="$t('userProfile.newPasswordRepeat')"
                v-model="passwordRepeat"
                :type="'password'"
                :error="passwordRepeatError"
                :errorMessage="$t('user.passwordShouldBeEqual')">
              </label-input>
            </div>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="4">
            <div class="variable-input-field submit-buttons align-right">
              <btn-pill
                :btnText="$t('commons.save')"
                v-on:click="onSave"
                btn-size="m">
              </btn-pill>
            </div>
          </grid-col>
        </grid-row>
      </form>

      <form
        v-on:submit.prevent
        v-on:keypress.enter.prevent="onSave">
        <grid-row column-align="center" col-spaced>
          <grid-col col-size="2">
            <h2 class="margin-top-m">{{ $t('personalArea.email') }}</h2>
            <div class="variable-input-field variable-input-field-name">
              <label-input
                form-size="m"
                :label="$t('personalArea.email')"
                v-model="emailInfo.email"
                :error="emailError"
                :errorMessage="$t('signin.notValidEmail')"
                v-on:input="editEmail"
                autocomplete="email">
                >
              </label-input>
            </div>
          </grid-col>
          <grid-col col-size="1">
            <div v-if=emailInfo.isVerificated
                 class=" margin-top-xl variable-input-field-name email-verified-status-ok ">
              <br>
              <br>
              <font-awesome-icon
                class="margin-top-xl fa-lg success"
                :icon="icon"/>
            </div>
          </grid-col>
          <grid-col col-size="1">
            <div class="center-vertical margin-top-m variable-input-field submit-buttons align-right">
              <btn-pill
                :disabled="emailInfo.isVerificated"
                :btnText="$t('personalArea.validateEmail')"
                v-on:click="onSaveEmail"
                btn-size="m">
              </btn-pill>
            </div>
          </grid-col>
        </grid-row>
      </form>
      <form
        v-on:submit.prevent
        v-on:keypress.enter.prevent="onSave">
        <grid-row column-align="center">
          <grid-col col-size="3">
            <h2 class="margin-top-m">{{ $t('health.userProfile.license') }}</h2>
            <h4>{{ $t('health.updateLicense.subscriptions') }}: {{ subscriptions }}</h4>
            <div class="variable-input-field variable-input-field-name">
              <label-input
                form-size="m"
                :label="$t('health.userProfile.license')"
                v-model="license"
                :error="licenseError"
                :errorMessage="$t('health.signin.notValidLicense')">
                >
              </label-input>
            </div>
          </grid-col>

          <grid-col col-size="1">
            <div class=" center-vertical margin-top-l variable-input-field submit-buttons align-right">
              <btn-pill
                :btnText="$t('health.userProfile.update')"
                v-on:click="onSaveLicense"
                btn-size="m">
              </btn-pill>
            </div>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="4">
            <div class=" center-vertical variable-input-field submit-buttons">
              <btn-pill
                :btnText="$t('health.userProfile.deleteAccount.title')"
                v-on:click="showConfirmationDeleteModal"
                btn-size="m">
              </btn-pill>
            </div>
          </grid-col>
        </grid-row>
      </form>
    </div>
    <ok-cancel-modal
      v-if="displayConfirmationDeleteModal"
      v-on:ok="onDeleteConfirm"
      v-on:cancel="onDeleteCancel"
      :title="$t('health.userProfile.deleteAccount.message')"
      :okButton="$t('commons.delete')"
      :cancelButton="$t('commons.cancel')">
    </ok-cancel-modal>
  </main-layout>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import LabelInput from '@/components/shared/forms/LabelInput';
import BtnPill from '@/components/shared/buttons/BtnPill';
import Checkbox from '@/components/shared/forms/Checkbox';
import Alert from '@/components/shared/alert/Alert';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {faCheckCircle} from '@fortawesome/fontawesome-free-solid';
import Chevron from '@/components/shared/Chevron';
import userApi from '@/api/user';
import licenseApi from 'BRAND/api/updateLicense';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';

export default {
  components: {
    MainLayout,
    GridRow,
    GridCol,
    LabelInput,
    BtnPill,
    Checkbox,
    Alert,
    FontAwesomeIcon,
    Chevron,
    OkCancelModal
  },
  data () {
    return {
      oldPassword: '',
      oldPasswordError: false,
      duplicatedEmail: false,
      password: '',
      passwordRepeat: '',
      license: '',
      subscriptions: '',
      displayConfirmationDeleteModal: false,
      /* Form field errors */
      passwordError: false,
      passwordRepeatError: false,
      emailError: false,
      licenseError: false,
      /* UI controls */
      alertVisible: false,
      alertMessage: '',
      alertType: '', // error, warning, success
      icon: faCheckCircle,
      emailInfo: {
        email: '',
        isVerificated: ''
      }
    };
  },
  created () {
    this.getEmailInfoByName();
  },
  computed: {
    ...mapGetters([
      'getUserAccount'
    ]),
    getBreadcrumb () {
      return [
        {
          name: this.$t('menu.settings'),
          link: '/settings'
        },
        {
          name: this.$t('personalArea.profile'),
          link: '/user-profile',
          disabled: true
        }
      ];
    }
  },
  methods: {
    ...mapActions([
      'updateUserPassword'
    ]),
    editEmail () {
      this.emailInfo.isVerificated = false;
    },
    onSave () {
      clearErrors(this);
      if (isValidForm(this)) {
        const request = {
          oldPassword: this.oldPassword,
          newPassword: this.password,
          username: this.getUserAccount.username
        };
        updateUserPassword(this, request);
      }
    },
    isValidEmail () {
      let valid = true;
      const regExp = /^([\w-.+]+@([\w-]+\.)+[\w-]{2,4})?$/;
      this.emailError = false;
      if (!this.emailInfo.email || !regExp.test(this.emailInfo.email.toLowerCase())) {
        valid = false;
        this.emailError = true;
      }

      return valid;
    },
    onSaveEmail () {
      clearErrors(this);
      if (this.isValidEmail()) {
        const request = {
          newEmail: this.emailInfo.email,
          username: this.getUserAccount.username
        };
        this.setEmailInfoByName(request);
      }
    },
    setEmailInfoByName (request) {
      userApi.setEmailInfoByName(this, request).then(() => {
        showAlert(this, this.$t('emailVerification.sendEmail', {name: request.username}), 'success');
      }).catch((error) => {
        if (error.status === 403) {
          this.oldPasswordError = true;
          showAlert(this, this.$t('emailVerification.errorSendEmail', {name: request.username}), 'error');
        } else if (error.status === 409) {
          this.duplicatedEmail = true;
          showAlert(this, this.$t('emailVerification.duplicatedEmail', {name: request.username}), 'error');
        }
      });
    },
    getEmailInfoByName () {
      userApi.getEmailInfoByName(this).then(data => {
        this.emailInfo = data;
      });
      licenseApi.getSubscriptions(this).then(data => {
        this.subscriptions = data.reverse().join(', ');
      });
    },
    onSaveLicense () {
      this.licenseError = false;
      if (this.license.length === 0) {
        this.licenseError = true;
      } else {
        licenseApi.updateLicense(this, encodeURIComponent(this.license)).then(data => {
          showAlert(this, this.$t('health.updateLicense.success'), 'success');
          this.license = '';
          licenseApi.getSubscriptions(this).then(data => {
            this.subscriptions = data.reverse().join(', ');
          });
        }).catch((error) => {
          if (error.status === 400) {
            this.licenseError = true;
          }
        });
      }
    },
    showConfirmationDeleteModal () {
      this.displayConfirmationDeleteModal = true;
    },
    onDeleteConfirm () {
      userApi.deleteMyAccount(this).then(() => {
        showAlert(this, this.$t('health.userProfile.deleteAccount.successMessage'), 'success');
        setTimeout(() => {
          this.logout();
        }, 2000);
      }).catch((error) => {
        if (error.status === 400 && error.body.message.includes('role')) {
          showAlert(this, this.$t('health.userProfile.deleteAccount.errorMessage') + ': ' + error.body.message, 'error');
        } else {
          showAlert(this, this.$t('health.userProfile.deleteAccount.errorMessage'), 'error');
        }
      });
      this.displayConfirmationDeleteModal = false;
    },
    onDeleteCancel () {
      this.displayConfirmationDeleteModal = false;
    },
    ...mapActions([
      'logout'
    ])
  }
};

const clearErrors = (context) => {
  context.oldPasswordError = false;
  context.passwordError = false;
  context.passwordRepeatError = false;
};

const isValidForm = (context) => {
  let valid = true;
  if (!(context.password && isValidPassword(context))) {
    valid = false;
    context.passwordError = true;
  }
  if (!(context.password === context.passwordRepeat)) {
    valid = false;
    context.passwordRepeatError = true;
  }
  return valid;
};

const isValidPassword = (context) => {
  const containsLetters = /[a-z]*[A-Z]*/.test(context.password);
  const containsNumbers = /\d/.test(context.password);
  const passwordLength = context.password.length >= 6;
  return (containsLetters === true && containsNumbers === true && passwordLength === true);
};

const updateUserPassword = (context, request) => {
  context.updateUserPassword({context: context, request}).then(() => {
    showAlert(context, context.$t('user.successUpdate', {name: request.username}), 'success');
  }).catch((error) => {
    if (error.status === 403) {
      context.oldPasswordError = true;
    }
    showAlert(context, context.$t('user.errorUpdate', {name: request.username}), 'error');
  });
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};

</script>

<style lang="scss" scoped>
@import "src/stylesheets/variables";

.email-verified-status-ok {
  color: $success;
}

.align-right {
  float: right;
  clear: both;
}

.variable-input-field {
  display: inline-block;
}

.variable-input-field-name {
  width: 100%;
  margin: 0 0;
}

.submit-buttons {
  float: right;
}

.center-vertical {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/HealthUserProfile.vue